import { Button } from '../../components/Button';
import { SubSelectCard } from '../../components/SubSelectCard';
import { Dropdown } from '../../components/Dropdown';
import { FloatingField } from '../../components/FloatingField';
import { FormCard } from '../../components/FormCard';
import { InfoNote } from '../../components/InfoNote';
import { SectionHeader } from '../../components/SectionHeader';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { StickyActionBar } from '../../components/StickyActionBar';
import { TimeChangeIcon } from '../../icons/TimeChangeIcon';
import { formatCurrency } from '../../utils/formatCurrency';
import { ActionBar } from '../ActionBar';
import { PreferencesPanel } from '../PreferencesPanel';
import {
  ActionBarArea,
  DateSection,
  EmptyTimeslots,
  FooterLeft,
  FooterRow,
  FooterTitle,
  FooterTotal,
  FieldCard,
  FormGroup,
  Heading,
  HeadingAccent,
  Inner,
  OutlinedCard,
  PreferencesCardBody,
  ScrollArea,
  Wrapper,
} from './PackageDiningContentPanel.styles';
import type { PackageDiningContentPanelProps } from './PackageDiningContentPanel.types';

const placeholderFor = (label: string, required?: boolean) => (required ? `${label}*` : label);

export function PackageDiningContentPanel({
  dateGroups,
  onMenuChange,
  onTimeslotChange,
  preferences = [],
  onPreferenceOptionToggle,
  onPreferenceOtherChange,
  onPreferenceFreeTextChange,
  onPreferenceExpandedChange,
  onBack,
  currentStep = 1,
  totalSteps,
  footerTitle,
  totalAmount,
  currencyCode,
  locale = 'en-GB',
  onNext,
  nextDisabled = false,
  isLoading = false,
  labels = {},
}: PackageDiningContentPanelProps) {
  const {
    title = 'Reserve a table.',
    titleAccent = "Pick a time, we'll save you a seat.",
    dateSubtitle = 'When would you like to eat?',
    backAriaLabel = 'Go back',
    progressLabel = 'Booking progress',
    menuLabel = 'Menu',
    timeLabel = 'Time',
    noTimeslots = 'No times available',
    otherButtonLabel,
    totalLabel = 'Total',
    nextLabel = 'Next',
  } = labels;

  return (
    <Wrapper aria-busy={isLoading || undefined}>
      <ScrollArea>
        <Inner>
          <ActionBar
            onBack={onBack}
            backAriaLabel={backAriaLabel}
            currentStep={currentStep}
            totalSteps={totalSteps}
            progressLabel={progressLabel}
          />
          {isLoading ? (
            <>
              <Heading>
                <SkeletonBlock variant="heading" />
              </Heading>
              <FormGroup>
                <SkeletonBlock height="220px" borderRadius="24px" />
                <SkeletonBlock height="220px" borderRadius="24px" />
              </FormGroup>
            </>
          ) : (
            <>
              <Heading>
                {title} <HeadingAccent>{titleAccent}</HeadingAccent>
              </Heading>
              <FormGroup>
                <OutlinedCard>
                  {dateGroups.map((group) => (
                    <DateSection key={group.id}>
                      <SectionHeader title={group.date} subtitle={dateSubtitle} />
                      {group.options.map((option) => (
                        <SubSelectCard key={option.id} heading={option.title}>
                          {option.menuOptions.length > 0 && (
                            <FieldCard>
                              <FormCard>
                                <FloatingField
                                  label={menuLabel}
                                  hasValue={Boolean(option.selectedMenu)}
                                  required={option.menuRequired}
                                >
                                  <Dropdown
                                    options={option.menuOptions}
                                    value={option.selectedMenu}
                                    placeholder={placeholderFor(menuLabel, option.menuRequired)}
                                    onChange={(value) => onMenuChange(option.id, value)}
                                    aria-label={`${menuLabel} ${option.title}`}
                                  />
                                </FloatingField>
                              </FormCard>
                            </FieldCard>
                          )}
                          {option.isLoading ? (
                            <SkeletonBlock height="72px" borderRadius="12px" />
                          ) : option.timeslotOptions.length ? (
                            <FieldCard>
                              <FormCard>
                                <FloatingField
                                  label={timeLabel}
                                  hasValue={Boolean(option.selectedTimeslot)}
                                >
                                  <Dropdown
                                    options={option.timeslotOptions}
                                    value={option.selectedTimeslot}
                                    placeholder={placeholderFor(timeLabel, true)}
                                    onChange={(value) => onTimeslotChange(option.id, value)}
                                    aria-label={`${timeLabel} ${option.title}`}
                                  />
                                </FloatingField>
                              </FormCard>
                            </FieldCard>
                          ) : (
                            <EmptyTimeslots>{noTimeslots}</EmptyTimeslots>
                          )}
                          {option.noteText && (
                            <InfoNote text={option.noteText} icon={<TimeChangeIcon size={18} />} />
                          )}
                        </SubSelectCard>
                      ))}
                    </DateSection>
                  ))}
                </OutlinedCard>
                {preferences.length > 0 && (
                  <OutlinedCard>
                    <PreferencesCardBody>
                      <PreferencesPanel
                        categories={preferences}
                        onOptionToggle={(categoryId, option) =>
                          onPreferenceOptionToggle?.(categoryId, option)
                        }
                        onOtherChange={(categoryId, value) =>
                          onPreferenceOtherChange?.(categoryId, value)
                        }
                        onFreeTextChange={(categoryId, value) =>
                          onPreferenceFreeTextChange?.(categoryId, value)
                        }
                        onExpandedChange={onPreferenceExpandedChange}
                        otherButtonLabel={otherButtonLabel}
                      />
                    </PreferencesCardBody>
                  </OutlinedCard>
                )}
              </FormGroup>
            </>
          )}
        </Inner>
      </ScrollArea>
      <ActionBarArea>
        <StickyActionBar>
          <FooterRow>
            <FooterLeft>
              {isLoading ? (
                <>
                  <SkeletonBlock width="140px" height="16px" />
                  <SkeletonBlock width="90px" height="20px" />
                </>
              ) : (
                <>
                  {footerTitle && <FooterTitle>{footerTitle}</FooterTitle>}
                  <FooterTotal>
                    {totalLabel} {formatCurrency(totalAmount, currencyCode, { locale })}
                  </FooterTotal>
                </>
              )}
            </FooterLeft>
            {isLoading ? (
              <SkeletonBlock width="96px" height="48px" borderRadius="24px" />
            ) : (
              <Button type="button" variant="primary" onClick={onNext} disabled={nextDisabled}>
                {nextLabel}
              </Button>
            )}
          </FooterRow>
        </StickyActionBar>
      </ActionBarArea>
    </Wrapper>
  );
}
